/**
 * Professional commercial-proposal PDF generator.
 *
 * Mirrors the engineering-report layout (trainReportPdf.ts): document-control
 * cover, numbered sections, word-wrapped prose, tinted equation panels and
 * tables that never bleed past the A4 margin. Every string is sanitised to
 * jsPDF's WinAnsi encoding so unicode (subscripts, Greek, dashes) renders
 * cleanly instead of as wide-spaced fallback glyphs.
 *
 * The generator reads LIVE data passed in from the Proposals tab — including
 * the user-edited price line items and editable intro / terms text — so the
 * PDF always reflects whatever is currently shown on screen.
 */
import { jsPDF } from 'jspdf';

/* ── WinAnsi sanitiser (shared pattern with trainReportPdf) ── */
function ascii(s: string): string {
  if (!s) return s;
  const map: Record<string, string> = {
    '₀':'0','₁':'1','₂':'2','₃':'3','₄':'4','₅':'5','₆':'6','₇':'7','₈':'8','₉':'9',
    '⁰':'0','¹':'1','²':'2','³':'3','⁴':'4','⁵':'5','⁶':'6','⁷':'7','⁸':'8','⁹':'9','⁺':'+','⁻':'-',
    'Δ':'D','δ':'d','μ':'u','µ':'u','α':'a','β':'b','γ':'g','Σ':'S','σ':'s','π':'pi',
    'Ω':'ohm','θ':'th','λ':'lam','ρ':'rho','τ':'tau','φ':'phi','·':'-','•':'-',
    '°':' deg','±':'+/-','×':'x','÷':'/','–':'-','—':'-','“':'"','”':'"','‘':"'",'’':"'",
    '≤':'<=','≥':'>=','≠':'!=','≈':'~','→':'->','←':'<-','⇒':'=>','√':'sqrt',
    '€':'EUR ','£':'GBP ','™':'(TM)','©':'(c)',
    'ą':'a','ć':'c','ę':'e','ł':'l','ń':'n','ó':'o','ś':'s','ż':'z','ź':'z',
    'Ą':'A','Ć':'C','Ę':'E','Ł':'L','Ń':'N','Ó':'O','Ś':'S','Ż':'Z','Ź':'Z',
  };
  return s.replace(/[^\x20-\x7E]/g, ch => map[ch] ?? '');
}

/* ── Public data shapes ── */
export interface ProposalLineItem {
  id: string;
  category: string;       // e.g. "Mechanical / process"
  description: string;
  qty: number;
  unitPrice: number;      // in the proposal currency
}
export interface ProposalProcessStep {
  title: string;
  body: string;
  equation?: { label: string; expression: string; legend: string };
  advantages?: string[];   // bullet list of benefits of this step
  compliance?: string;     // standard / regulatory note for this step
}
export interface ProposalAutomation {
  enabled: boolean;
  optional: boolean;       // true => present as an optional upgrade, false => included
  headline: string;        // one-line summary
  scope: string[];         // hardware/software scope bullets
  io: { di: number; do: number; ai: number; ao: number };
  controlNarrative: string[];
  compliance: string[];
  priceNote?: string;      // e.g. "Included in Section 6" or "Optional uplift, POA"
}
export interface ProposalPdfInput {
  optionLabel: string;            // "Option B — Recommended"
  optionTagline: string;          // one-line subtitle
  proposalNumber: string;
  date: string;
  customerName: string;
  siteName: string;
  preparedBy: string;
  preparedFor: string;
  currencySymbol: string;         // "GBP " (ascii-safe)
  intro: string;                  // editable
  scope: string[];                // bullet list (editable)
  processSteps: ProposalProcessStep[];
  influent: { flow: number; fe: number; mn: number; sdi: number; turbidity: number; ph: number; temp: number };
  finalWater: { fe: number; mn: number; sdi: number; turbidity: number };
  meetsRO: boolean;
  designNote?: string;            // caveat shown under the design-basis performance table
  lineItems: ProposalLineItem[];  // editable pricing
  terms: { leadTime: string; payment: string; validity: string; warranty: string; delivery: string };
  exclusions: string[];
  notes: string;                  // editable closing notes
  automation?: ProposalAutomation;
  regulatory?: string[];          // standards & regulatory basis bullets
  benefits?: string[];            // key benefits / why choose this option
  aerationRationale?: string[];   // extensive "why aeration / iron removal is required" prose
  membraneLimits?: Array<{ membrane: string; feLimit: string; note: string }>; // RO membrane Fe limits
  basisCaveat?: string[];         // basis-of-design / influent-validity caveat
  assumptions?: string[];         // itemised basis on which the quote is built (listed first)
  infoRequired?: string[];        // information required from the customer to firm up the quote
  processGuarantee?: string[];    // written process / performance guarantee
  optionalUpgrades?: { label: string; price: number | null }[]; // priced options, NOT in the headline total (null = POA)
  installationImages?: { data: string; aspect: number }[]; // small reference photos shown at the foot of the proposal
}

/* ── Layout constants ── */
const PAGE_W = 210, PAGE_H = 297, M = 18;
const CONTENT_W = PAGE_W - 2 * M;
const BOTTOM_LIMIT = PAGE_H - 22;
// Reynolds & Bauhm blue brand palette. No black/near-black backgrounds; gold kept
// only as a small secondary accent. Equations sit on a light-blue panel.
const C = {
  BRAND:'#1d4ed8',    // R&B primary blue — bars, rules, accents
  GOLD:'#cda42e',     // secondary accent (yellow is permitted)
  INK:'#0f172a',      // body text only — never used as a fill
  MUTED:'#475569', RULE:'#cbd5e1',
  GOOD:'#059669', BAD:'#dc2626',
  PANEL:'#eaf1ff',    // light-blue table header tint
  EQBG:'#dbeafe',     // light-blue equation background
  EQ:'#10337a',       // dark-blue equation text (reads on light blue)
  NAVY:'#0f2c66',     // deep blue cover background (replaces black)
  NAVY2:'#1b3f86',    // lighter blue control card on cover
};
function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
}
const setFill = (d: jsPDF, h: string) => { const [r,g,b]=rgb(h); d.setFillColor(r,g,b); };
const setDraw = (d: jsPDF, h: string) => { const [r,g,b]=rgb(h); d.setDrawColor(r,g,b); };
const setText = (d: jsPDF, h: string) => { const [r,g,b]=rgb(h); d.setTextColor(r,g,b); };
const fnt = (d: jsPDF, fam: 'helvetica'|'courier', st: 'normal'|'bold'|'italic', sz: number) => { d.setFont(fam, st); d.setFontSize(sz); };

function money(sym: string, n: number): string {
  return ascii(sym) + Math.round(n).toLocaleString('en-GB');
}

class Layout {
  doc: jsPDF; y = 36; pageNo = 1; sectionTitle = ''; baseTitle = '';
  proposalNumber: string; rev = 'R0'; date: string; optionLabel: string;
  constructor(doc: jsPDF, o: { proposalNumber: string; date: string; optionLabel: string }) {
    this.doc = doc; this.proposalNumber = ascii(o.proposalNumber); this.date = ascii(o.date); this.optionLabel = ascii(o.optionLabel);
  }
  startPage(title: string) { this.baseTitle = ascii(title); this.sectionTitle = this.baseTitle; this.y = 36; this.header(); this.footer(); }
  header() {
    const d = this.doc;
    setFill(d, C.BRAND); d.rect(0,0,PAGE_W,6,'F');
    setText(d, C.INK); fnt(d,'helvetica','bold',11); d.text(this.sectionTitle, M, 14);
    setText(d, C.MUTED); fnt(d,'helvetica','normal',8.5);
    d.text(this.optionLabel, M, 20);
    fnt(d,'helvetica','normal',8);
    d.text(`Proposal ${this.proposalNumber}`, PAGE_W - M, 14, { align:'right' });
    d.text(`Rev. ${this.rev}  ·  ${this.date}`.replace('·','-'), PAGE_W - M, 19, { align:'right' });
    setDraw(d, C.RULE); d.setLineWidth(0.3); d.line(M,28,PAGE_W-M,28);
  }
  footer() {
    const d = this.doc;
    setDraw(d, C.RULE); d.line(M, PAGE_H-15, PAGE_W-M, PAGE_H-15);
    setText(d, C.INK); fnt(d,'helvetica','bold',6.3);
    d.text(ascii('Reynolds & Bauhm Ltd · Tel +44 (0)1278 258021 · Mobile 07542 743093 · office@reynoldsbauhm.co.uk · www.reynoldsbauhm.co.uk · Co. 10436543'), PAGE_W/2, PAGE_H-10.5, { align:'center', maxWidth: CONTENT_W });
    setText(d, C.MUTED); fnt(d,'helvetica','normal',6.3);
    d.text(ascii('Commercial proposal — budgetary, subject to detailed design, site survey and final water analysis. Prices exclude VAT unless stated.'), PAGE_W/2, PAGE_H-6.5, { align:'center', maxWidth: CONTENT_W });
  }
  room(need: number) { if (this.y + need > BOTTOM_LIMIT) { this.doc.addPage(); this.pageNo++; this.sectionTitle = this.baseTitle + ' (cont.)'; this.y = 36; this.header(); this.footer(); } }
  heading(text: string, level: 1|2|3 = 1) {
    text = ascii(text);
    const sz = { 1:13, 2:11, 3:9.5 }[level]; const gap = { 1:8, 2:6, 3:5 }[level];
    this.room(gap+4);
    setText(this.doc, C.INK); fnt(this.doc,'helvetica','bold',sz);
    const w = this.doc.splitTextToSize(text, CONTENT_W);
    this.doc.text(w, M, this.y); this.y += w.length*(sz*0.45)+1;
    if (level===1) { setDraw(this.doc, C.BRAND); this.doc.setLineWidth(0.6); this.doc.line(M,this.y,M+36,this.y); this.y+=3; }
    else this.y += 1;
  }
  para(text: string, o?: { italic?: boolean; size?: number; color?: string }) {
    text = ascii(text);
    const sz = o?.size ?? 9.5, lh = sz*0.45;
    const style: 'italic'|'normal' = o?.italic ? 'italic' : 'normal';
    fnt(this.doc,'helvetica', style, sz);
    const lines = this.doc.splitTextToSize(text, CONTENT_W);
    for (const line of lines) {
      this.room(lh+0.5);
      // Re-apply font/colour each line: room() may have started a new page, whose
      // header/footer leave the font at the small footer size — otherwise the tail
      // of a paragraph that crosses a page break renders in the wrong (smaller) font.
      fnt(this.doc,'helvetica', style, sz); setText(this.doc, o?.color ?? C.INK);
      this.doc.text(line, M, this.y); this.y += lh;
    }
    this.y += 2;
  }
  bullet(text: string, sz = 9) {
    text = ascii(text); const lh = sz*0.45;
    fnt(this.doc,'helvetica','normal',sz); setText(this.doc, C.INK);
    const w = this.doc.splitTextToSize(text, CONTENT_W-6);
    // Keep the whole bullet together — never split a bullet across a page break.
    this.room(w.length*lh + 1.5);
    fnt(this.doc,'helvetica','normal',sz); setText(this.doc, C.INK);
    w.forEach((ln: string, i: number) => { if (i===0) this.doc.text('-', M, this.y); this.doc.text(ln, M+4, this.y); this.y += lh; });
    this.y += 1;
  }
  kv(k: string, v: string) {
    k = ascii(k); v = ascii(v);
    const kw = 50, vw = CONTENT_W-kw-2;
    fnt(this.doc,'helvetica','normal',9);
    const kl = this.doc.splitTextToSize(k, kw), vl = this.doc.splitTextToSize(v, vw);
    const rows = Math.max(kl.length, vl.length), h = rows*4.4;
    this.room(h+1.5);
    fnt(this.doc,'helvetica','normal',9); // re-apply after possible page break (header/footer reset the font)
    setText(this.doc, C.MUTED); this.doc.text(kl, M, this.y);
    setText(this.doc, C.INK); this.doc.text(vl, M+kw+2, this.y);
    this.y += h + 1.5;
  }
  equation(label: string, expression: string, legend: string) {
    label=ascii(label); expression=ascii(expression); legend=ascii(legend);
    const pad=4, ef=9, lf=8;
    const ew = this.doc.splitTextToSize(expression, CONTENT_W-2*pad);
    const lw = this.doc.splitTextToSize(legend, CONTENT_W-2*pad);
    const h = pad+4+ew.length*4+3+lw.length*3.6+pad;
    this.room(h+2);
    setFill(this.doc, C.EQBG); setDraw(this.doc,'#93c5fd'); this.doc.setLineWidth(0.4);
    this.doc.roundedRect(M, this.y, CONTENT_W, h, 2, 2, 'FD');
    setText(this.doc, C.BRAND); fnt(this.doc,'helvetica','bold',8.5); this.doc.text(label, M+pad, this.y+pad+2);
    setText(this.doc, C.EQ); fnt(this.doc,'courier','bold',ef); this.doc.text(ew, M+pad, this.y+pad+8);
    setText(this.doc, C.MUTED); fnt(this.doc,'helvetica','italic',lf); this.doc.text(lw, M+pad, this.y+pad+8+ew.length*4+3);
    this.y += h + 7;   // extra clearance so following prose never touches the panel
  }
  table(headers: string[], rows: string[][], widths: number[], aligns?: ('l'|'r')[]) {
    headers = headers.map(ascii); rows = rows.map(r => r.map(ascii));
    const sum = widths.reduce((a,b)=>a+b,0);
    if (sum > CONTENT_W) { const k = CONTENT_W/sum; widths = widths.map(w=>w*k); }
    const headerH = 6.5;
    this.room(headerH+6);
    setFill(this.doc, C.PANEL); this.doc.rect(M, this.y-4, CONTENT_W, headerH, 'F');
    setText(this.doc, C.INK); fnt(this.doc,'helvetica','bold',8.5);
    let x = M;
    headers.forEach((h,i) => {
      const a = aligns?.[i] ?? 'l';
      const w = this.doc.splitTextToSize(h, widths[i]-2);
      this.doc.text(w[0] ?? '', a==='r' ? x+widths[i]-1.5 : x+1.5, this.y, { align: a==='r'?'right':'left' });
      x += widths[i];
    });
    this.y += headerH-2;
    setDraw(this.doc, C.RULE); this.doc.setLineWidth(0.2); this.doc.line(M,this.y-2,M+CONTENT_W,this.y-2);
    fnt(this.doc,'helvetica','normal',8.5);
    rows.forEach(row => {
      const cells = row.map((c,i)=>this.doc.splitTextToSize(c, widths[i]-2));
      const lc = Math.max(...cells.map(c=>c.length)); const h = lc*4.2+1;
      this.room(h+1);
      x = M; setText(this.doc, C.INK);
      cells.forEach((w,i) => {
        const a = aligns?.[i] ?? 'l';
        this.doc.text(w, a==='r' ? x+widths[i]-1.5 : x+1.5, this.y+2, { align: a==='r'?'right':'left' });
        x += widths[i];
      });
      this.y += h; this.doc.line(M,this.y-1,M+CONTENT_W,this.y-1);
    });
    this.y += 4;
  }
  spacer(h: number) { this.y += h; }
  /* Price total rendered as plain text between rules (no filled box). */
  priceLine(label: string, amount: string, opts?: { big?: boolean }) {
    const sz = opts?.big ? 14 : 11.5;
    this.room(opts?.big ? 18 : 12);
    setDraw(this.doc, C.BRAND); this.doc.setLineWidth(opts?.big ? 0.7 : 0.5);
    this.doc.line(M, this.y, M + CONTENT_W, this.y); this.y += opts?.big ? 8 : 6.5;
    setText(this.doc, C.INK); fnt(this.doc, 'helvetica', 'bold', sz);
    this.doc.text(ascii(label), M, this.y);
    setText(this.doc, C.BRAND); fnt(this.doc, 'helvetica', 'bold', sz + 1);
    this.doc.text(ascii(amount), PAGE_W - M, this.y, { align: 'right' });
    this.y += opts?.big ? 4 : 3.5;
    setDraw(this.doc, C.RULE); this.doc.setLineWidth(0.3);
    this.doc.line(M, this.y, M + CONTENT_W, this.y); this.y += 4;
  }
}

export function generateProposalPdf(d: ProposalPdfInput): jsPDF {
  const doc = new jsPDF({ unit:'mm', format:'a4' });
  const L = new Layout(doc, { proposalNumber: d.proposalNumber, date: d.date, optionLabel: d.optionLabel });
  const sym = d.currencySymbol || 'GBP ';

  /* totals */
  const lineTotal = (li: ProposalLineItem) => li.qty * li.unitPrice;
  const grand = d.lineItems.reduce((s, li) => s + lineTotal(li), 0);
  // group by category, preserving first-seen order
  const cats: string[] = [];
  d.lineItems.forEach(li => { if (!cats.includes(li.category)) cats.push(li.category); });
  const catSubtotal = (c: string) => d.lineItems.filter(li=>li.category===c).reduce((s,li)=>s+lineTotal(li),0);

  /* ── COVER (white background, blue brand accents only) ── */
  setFill(doc, '#ffffff'); doc.rect(0,0,PAGE_W,PAGE_H,'F');
  setFill(doc, C.BRAND); doc.rect(0,0,PAGE_W,14,'F');
  doc.setTextColor(255,255,255); fnt(doc,'helvetica','bold',10); doc.text('REYNOLDS & BAUHM LTD', M, 9);
  { const wName = doc.getTextWidth('REYNOLDS & BAUHM LTD'); fnt(doc,'helvetica','normal',8); doc.text(ascii('· SINCE 2016'), M + wName + 3, 9); }
  doc.setTextColor(255,255,255); fnt(doc,'helvetica','normal',8); doc.text(ascii('CONFIDENTIAL · COMMERCIAL PROPOSAL'), PAGE_W-M, 9, { align:'right' });
  setText(doc, C.INK); fnt(doc,'helvetica','bold',28); doc.text('Commercial', M, 74); doc.text('Proposal', M, 88);
  setDraw(doc, C.GOLD); doc.setLineWidth(1.1); doc.line(M, 93, M+48, 93);   // gold brand accent
  setText(doc, C.BRAND); fnt(doc,'helvetica','bold',14);
  doc.text(doc.splitTextToSize(ascii(d.optionLabel), PAGE_W-2*M), M, 104);
  setText(doc, C.MUTED); fnt(doc,'helvetica','italic',10);
  doc.text(doc.splitTextToSize(ascii(d.optionTagline), PAGE_W-2*M), M, 116);

  setFill(doc, '#ffffff'); setDraw(doc, C.BRAND); doc.setLineWidth(0.4);
  doc.roundedRect(M, 150, PAGE_W-2*M, 84, 3, 3, 'FD');
  setText(doc, C.BRAND); fnt(doc,'helvetica','bold',9); doc.text('PROPOSAL CONTROL', M+5, 160);
  const dc: Array<[string,string]> = [
    ['Proposal no.', d.proposalNumber], ['Revision','R0 — Issued for review'], ['Date', d.date],
    ['Customer', d.customerName], ['Site', d.siteName], ['Prepared by', d.preparedBy], ['Issued to', d.preparedFor],
  ];
  dc.forEach(([k,v],i) => {
    setText(doc, C.MUTED); fnt(doc,'helvetica','normal',9.5); doc.text(ascii(k), M+5, 170+i*8);
    setText(doc, C.INK); doc.text((doc.splitTextToSize(ascii(v), PAGE_W-2*M-52)[0] ?? ''), M+52, 170+i*8);
  });

  // (No headline price on the cover — the budget price band is presented on the final page.)
  setText(doc, C.MUTED); fnt(doc,'helvetica','italic',9);
  doc.text(ascii('Contents overleaf. Scope, design basis and the full price breakdown follow; the summary budget price is given on the final page.'), M, 250, { maxWidth: PAGE_W-2*M });

  setFill(doc, C.BRAND); doc.rect(0, PAGE_H-12, PAGE_W, 12, 'F');
  doc.setTextColor(255,255,255); fnt(doc,'helvetica','bold',8);
  doc.text(ascii('REYNOLDS & BAUHM LTD  ·  Co. No. 10436543 (England & Wales)'), PAGE_W/2, PAGE_H-7, { align:'center' });
  fnt(doc,'helvetica','normal',7.5);
  doc.text(ascii('Tel +44 (0)1278 258021  ·  Mobile 07542 743093  ·  office@reynoldsbauhm.co.uk  ·  www.reynoldsbauhm.co.uk'), PAGE_W/2, PAGE_H-3, { align:'center' });

  /* ── 2. Table of contents (reserved page; entries written at the end once
   *    final page numbers are known) ── */
  doc.addPage(); L.pageNo = 2; L.startPage('Contents');
  const TOC_PAGE = 2;
  const toc: Array<{ title: string; page: number }> = [];
  const tocMark = (title: string) => toc.push({ title, page: L.pageNo });

  /* ── 1. Introduction ── */
  doc.addPage(); L.pageNo = 3; L.startPage('Introduction & Scope');
  tocMark('Introduction, basis of quotation & scope of supply');
  L.heading('1. Introduction', 1);
  L.para(d.intro);
  if (d.assumptions && d.assumptions.length) {
    L.spacer(1);
    L.heading('1.1  Basis and assumptions of this quotation (read first)', 2);
    L.para('This quotation is built on the assumptions listed below. If any of them change, the quotation must be amended and re-issued.');
    d.assumptions.forEach((a, i) => L.bullet(`${i + 1}. ${a}`));
  }
  if (d.basisCaveat && d.basisCaveat.length) {
    L.spacer(1);
    L.heading('1.2  Effect of the influent sample (important)', 2);
    d.basisCaveat.forEach(p => L.para(p, { italic: true, color: C.MUTED, size: 9.5 }));
  }
  if (d.infoRequired && d.infoRequired.length) {
    L.spacer(1);
    L.heading('1.3  Information required from the customer', 2);
    L.para('We do not currently hold a correct final / treated water sample. To remove the assumptions above and issue a firm, guaranteed price, the following information is required:');
    d.infoRequired.forEach((r, i) => L.bullet(`${i + 1}. ${r}`));
  }
  L.spacer(1);
  L.heading('2. Scope of supply', 2);
  d.scope.forEach(s => L.bullet(s));
  if (d.benefits && d.benefits.length) {
    L.spacer(1);
    L.heading('2.1  Key benefits of this option', 2);
    L.para('The configuration delivers the following commercial, operational and compliance benefits:');
    d.benefits.forEach(b => L.bullet(b, 8.5));
  }
  if (d.processGuarantee && d.processGuarantee.length) {
    L.spacer(1);
    L.heading('2.2  Process / performance guarantee', 2);
    d.processGuarantee.forEach(p => L.para(p));
  }
  if (d.aerationRationale && d.aerationRationale.length) {
    L.spacer(1);
    L.heading('2.3  Why iron removal and aeration are required (and how it depends on the RO membrane)', 2);
    d.aerationRationale.forEach(p => L.para(p));
    if (d.membraneLimits && d.membraneLimits.length) {
      L.spacer(1);
      L.heading('Typical RO membrane iron feed-water limits (to be confirmed against the element data sheet)', 3);
      L.table(
        ['RO membrane', 'Total iron limit', 'Note'],
        d.membraneLimits.map(m => [m.membrane, m.feLimit, m.note]),
        [52, 36, 86],
      );
    }
  }

  /* ── 3. Process description (scientific, step by step) ── */
  doc.addPage(); L.pageNo++; L.startPage('Process Description');
  tocMark('Process description & scientific basis');
  L.heading('3. Process description and scientific basis', 1);
  L.para('The treatment process is described below in flow order. Each step sets out the engineering function, the governing physical or chemical principle, the design correlation used to size the equipment, the specific advantages the step delivers, and the standard or regulatory basis it satisfies. The cumulative effect of the steps is to deliver water that meets the downstream reverse-osmosis feed-water envelope set out in Section 4.');
  d.processSteps.forEach((st, i) => {
    L.spacer(1);
    L.heading(`3.${i+1}  ${st.title}`, 2);
    L.para(st.body);
    if (st.equation) L.equation(st.equation.label, st.equation.expression, st.equation.legend);
    if (st.advantages && st.advantages.length) {
      L.heading('Advantages of this step', 3);
      st.advantages.forEach(a => L.bullet(a));
    }
    if (st.compliance) {
      L.heading('Compliance basis', 3);
      L.para(st.compliance, { italic: true, size: 9, color: C.MUTED });
    }
  });

  /* ── 4. Design basis & guaranteed performance ── */
  doc.addPage(); L.pageNo++; L.startPage('Design Basis & Performance');
  tocMark('Design basis & guaranteed performance');
  L.heading('4. Design basis and guaranteed performance', 1);
  L.para('The plant is designed against the measured raw-water characteristics and the downstream reverse-osmosis feed-water envelope. The table below states the design influent and the treated-water quality this configuration is engineered to deliver.');
  L.table(
    ['Parameter', 'Design influent', 'Treated (to RO)', 'RO target'],
    [
      ['Design flow (m3/h)', d.influent.flow.toFixed(1), '-', '-'],
      ['Iron, Fe (mg/L)', d.influent.fe.toFixed(3), d.finalWater.fe.toFixed(3), '<= 0.05'],
      ['Manganese, Mn (mg/L)', d.influent.mn.toFixed(3), d.finalWater.mn.toFixed(3), '<= 0.05'],
      ['SDI15 (-)', d.influent.sdi.toFixed(1), d.finalWater.sdi.toFixed(1), '<= 3'],
      ['Turbidity (NTU)', d.influent.turbidity.toFixed(2), d.finalWater.turbidity.toFixed(2), '<= 1'],
      ['pH (-)', d.influent.ph.toFixed(1), '-', '6.5-9.0'],
      ['Temperature (deg C)', d.influent.temp.toFixed(0), '-', '-'],
    ],
    [58, 40, 40, 36], ['l','r','r','r'],
  );
  L.para(d.meetsRO
    ? 'On the simulated performance above, the treated water meets the reverse-osmosis feed-water envelope for iron, manganese and SDI.'
    : 'NOTE: on the simulated performance above, one or more parameters do not yet meet the reverse-osmosis feed-water envelope; see the process notes for the residual item and the recommended mitigation.',
    { italic: true, color: d.meetsRO ? C.GOOD : C.BAD, size: 9 });
  if (d.designNote) L.para(d.designNote, { italic: true, color: C.MUTED, size: 9 });

  if (d.regulatory && d.regulatory.length) {
    L.spacer(1);
    L.heading('4.1  Standards and regulatory basis', 2);
    L.para('The design, equipment and discharge framework for this proposal are referenced to the following standards and regulators:');
    d.regulatory.forEach(r => L.bullet(r));
  }

  /* ── 5. Optional automation — Siemens SIMATIC S7-1500 ── */
  let secNo = 5;
  if (d.automation && d.automation.enabled) {
    const a = d.automation;
    doc.addPage(); L.pageNo++; L.startPage('Automation — Siemens SIMATIC S7-1500');
    tocMark(`${a.optional ? 'Optional automation upgrade' : 'Automation & control'} — Siemens SIMATIC S7-1500`);
    L.heading(`${secNo}. ${a.optional ? 'Optional automation upgrade' : 'Automation & control'} — Siemens SIMATIC S7-1500`, 1);
    L.para(a.headline);
    if (a.optional) {
      L.para('This automation package is offered as an OPTIONAL upgrade to the base scope. It can be added at order stage; the base plant operates with the standard control panel described in Section 2.', { italic: true, size: 9, color: C.MUTED });
    }
    L.heading('Automation scope of supply', 2);
    a.scope.forEach(s => L.bullet(s));
    L.heading('Input / output schedule (indicative)', 2);
    L.table(
      ['I/O type', 'Channels', 'Typical signals'],
      [
        ['Digital inputs (DI)',  String(a.io.di), 'valve position (single limit), level switches, motor run/fault'],
        ['Digital outputs (DO)', String(a.io.do), 'valve solenoids, pump/blower starts (DOL), alarm/lamp'],
        ['Analogue inputs (AI)', String(a.io.ai), 'flow, pressure, dP, level — and pH/ORP where fitted (4-20 mA)'],
        ['Analogue outputs (AO)',String(a.io.ao), 'VFD speed references where fitted (no chemical dosing — media self-regenerates on dissolved oxygen)'],
      ],
      [50, 28, 96], ['l','r','l'],
    );
    L.heading('Control philosophy', 2);
    a.controlNarrative.forEach(c => L.bullet(c));
    L.heading('Compliance & standards', 2);
    a.compliance.forEach(c => L.bullet(c));
    if (a.priceNote) L.para('Commercial: ' + a.priceNote, { italic: true, size: 9, color: C.MUTED });
    secNo = 6;
  }

  /* ── Price breakdown ── */
  doc.addPage(); L.pageNo++; L.startPage('Price Breakdown');
  tocMark('Price breakdown');
  L.heading(`${secNo}. Price breakdown`, 1);
  L.para('The budget price is broken down below by work package. Quantities and unit prices are budgetary and will be confirmed at detailed-design stage. All prices exclude VAT.');
  // Categories the client sees as a single "complete package" line (the detailed
  // breakdown is kept internally on the Proposals tab, not exposed to the client).
  const PACKAGE_CATS = ['Mechanical / process'];
  cats.forEach(cat => {
    L.heading(cat, 3);
    if (PACKAGE_CATS.includes(cat)) {
      // Complete package: list the equipment (with sizes / descriptions) but a single package price.
      L.para('Supplied as a complete process package comprising the following equipment:');
      d.lineItems.filter(li => li.category === cat).forEach(li => {
        const q = li.qty > 1 ? `${li.qty} x ` : '';
        L.bullet(`${q}${li.description}`);
      });
      L.priceLine('Complete process package — supply, delivery & assembly', money(sym, catSubtotal(cat)));
    } else {
      const rows = d.lineItems.filter(li => li.category === cat).map(li => [
        li.description,
        String(li.qty),
        money(sym, li.unitPrice),
        money(sym, lineTotal(li)),
      ]);
      rows.push(['Subtotal — ' + cat, '', '', money(sym, catSubtotal(cat))]);
      L.table(['Item', 'Qty', 'Unit price', 'Amount'], rows, [96, 16, 30, 32], ['l','r','r','r']);
    }
  });
  L.spacer(1);
  // grand total — plain text between rules (no filled box)
  L.priceLine('TOTAL (ex. VAT)', money(sym, grand));

  if (d.optionalUpgrades && d.optionalUpgrades.length) {
    L.spacer(1);
    L.heading('Optional upgrades (priced separately — not included in the total above)', 3);
    d.optionalUpgrades.forEach(u => L.kv(u.price == null ? 'POA' : `+ ${money(sym, u.price)}`, u.label));
  }

  /* ── Commercial terms ── */
  doc.addPage(); L.pageNo++; L.startPage('Commercial Terms');
  tocMark('Commercial terms, exclusions, notes & budget price');
  L.heading(`${secNo + 1}. Commercial terms`, 1);
  L.kv('Delivery', d.terms.delivery);
  L.kv('Lead time', d.terms.leadTime);
  L.kv('Payment terms', d.terms.payment);
  L.kv('Validity', d.terms.validity);
  L.kv('Warranty', d.terms.warranty);
  L.spacer(2);
  L.heading(`${secNo + 2}. Exclusions`, 2);
  d.exclusions.forEach(x => L.bullet(x));
  if (d.notes && d.notes.trim()) {
    L.spacer(2);
    L.heading(`${secNo + 3}. Notes`, 2);
    L.para(d.notes);
  }
  L.spacer(2);
  L.para('This proposal is issued for budgetary and planning purposes. It does not constitute a contract. Final pricing, guarantees and terms are subject to detailed design, a site survey, and confirmation of the raw-water analysis. Reynolds & Bauhm Ltd looks forward to progressing this enquiry.', { italic: true, size: 8.5, color: C.MUTED });

  // ── Headline budget price (presented here, on the final page) — plain text, no box ──
  L.spacer(3);
  L.priceLine('TOTAL BUDGET PRICE (ex. VAT)', money(sym, grand), { big: true });

  // ── Example UK installations (small reference photos, optional) ──
  if (d.installationImages && d.installationImages.length) {
    const imgs = d.installationImages.slice(0, 3);
    const gap = 4;
    const w = (CONTENT_W - gap * (imgs.length - 1)) / imgs.length;
    const dims = imgs.map(im => Math.min(w / (im.aspect || 1.4), 30)); // cap height ~30 mm
    const maxH = Math.max(...dims);
    L.spacer(3); L.room(maxH + 12);
    setText(doc, C.INK); fnt(doc, 'helvetica', 'bold', 8.5);
    doc.text(ascii('Example UK installations'), M, L.y); L.y += 3;
    let x = M;
    imgs.forEach((im, i) => {
      const fmt = im.data.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      try { doc.addImage(im.data, fmt, x, L.y, w, dims[i]); } catch { /* skip bad image */ }
      x += w + gap;
    });
    L.y += maxH + 2;
    setText(doc, C.MUTED); fnt(doc, 'helvetica', 'italic', 7);
    doc.text(ascii('Photographs of comparable UK installations, for illustration only.'), M, L.y); L.y += 4;
  }

  /* ── Table of contents — written now that final page numbers are known ── */
  doc.setPage(TOC_PAGE);
  let ty = 40;
  setText(doc, C.INK); fnt(doc, 'helvetica', 'bold', 15); doc.text('Contents', M, ty); ty += 4;
  setDraw(doc, C.BRAND); doc.setLineWidth(0.6); doc.line(M, ty, M + 40, ty); ty += 9;
  toc.forEach((e, i) => {
    fnt(doc, 'helvetica', 'normal', 10.5); setText(doc, C.INK);
    const label = `${i + 1}.  ${ascii(e.title)}`;
    doc.text(label, M + 2, ty);
    setText(doc, C.MUTED); doc.text(String(e.page), PAGE_W - M - 2, ty, { align: 'right' });
    // dotted leader
    setDraw(doc, C.RULE); doc.setLineWidth(0.15);
    const lw = doc.getTextWidth(label);
    doc.setLineDashPattern([0.6, 1.2], 0); doc.line(M + 4 + lw, ty - 1, PAGE_W - M - 8, ty - 1); doc.setLineDashPattern([], 0);
    ty += 9;
  });

  /* ── page numbers ── */
  const total = doc.getNumberOfPages();
  for (let p = 2; p <= total; p++) {
    doc.setPage(p);
    setFill(doc, '#ffffff'); doc.rect(PAGE_W-M-34, 21, 34, 4.5, 'F');
    setText(doc, C.MUTED); fnt(doc,'helvetica','normal',8);
    doc.text(ascii(`Page ${p} of ${total}`), PAGE_W-M, 24, { align:'right' });
  }
  return doc;
}
